<template>
  <layout title="Statistics">
    <div class="flex justify-center">
      <div class="md:p-8 p-4 !pb-28 max-w-7xl w-full flex items-stretch">
        <sideMenu />
        <div class="flex flex-col flex-1 md:ml-8 ml-4">
          <div class="sm:grid grid-flow-col lg:grid-cols-3 grid-cols-2 gap-6">
            <div
              class="font-black text-3xl text-gray-800 inline-block lg:col-span-2 col-span-1"
            >
              Statistics
            </div>
            <div
              class="col-span-1 w-full bg-red shadow-allArround flex rounded-xl mt-6 sm:mt-0"
              v-if="Object.keys(currPlan).length > 0"
            >
              <MonthPickerInput
                class="flex flex-col flex-1 z-20"
                lang="en"
                :clearable="true"
                :editable-year="false"
                variant="default"
                :show-year="true"
                :highlight-exact-date="false"
                :year-only="false"
                :default-month="d.getMonth() + 1"
                :default-month-range="[d.getMonth(), d.getMonth()]"
                :default-year="d.getFullYear()"
                :input-pre-filled="true"
                :range="true"
                @change="showDate"
              >
              </MonthPickerInput>
              <div
                class="flex items-center px-1.5 rounded-r-xl cursor-pointer"
                style="border: 1px solid #ccc"
              >
                <CalendarIcon />
              </div>
            </div>
          </div>

          <div class="flex flex-1" v-if="Object.keys(currPlan).length > 0">
            <div
              class="mt-6 bg-white shadow-allArround rounded-xl flex-1 relative"
            >
              <PuSkeleton
                class="h-full w-full z-20 rounded-xl absolute"
                v-if="statisticsMonthLoading && statisticsErrorMonthLoading"
              />
              <div
                class="p-4 bg-gray-200 font-bold text-xl rounded-t-xl sm:flex block justify-between items-center"
              >
                <div>Monthly Statistics</div>
              </div>

              <div
                class="p-4"
                v-if="!statisticsMonthLoading && !statisticsErrorMonthLoading"
              >
                <barChart
                  :labels="monthLabels"
                  :data="monthConsumption"
                  :errorData="errorDataMonth"
                  :render="renderLine"
                  :height="300"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-1" v-if="Object.keys(currPlan).length > 0">
            <div
              class="mt-6 bg-white shadow-allArround rounded-xl flex-1 relative"
              v-if="showDailyChart"
            >
              <PuSkeleton
                class="h-full w-full z-20 rounded-xl absolute"
                v-if="statisticsDailyLoading & statisticsErrorDailyLoading"
              />
              <div
                class="p-4 bg-gray-200 font-bold text-xl rounded-t-xl sm:flex block justify-between items-center"
              >
                <div>Daily Statistics</div>
              </div>
              <div class="p-4">
                <lineChartDay
                  :labels="dailyLabels"
                  :data="dailyConsumption"
                  :errorData="errorDataDaily"
                  :render="renderLineDaily"
                  :height="400"
                />
              </div>
            </div>
          </div>
          <div
            class="mt-6 bg-white shadow-allArround rounded-xl flex-1 relative p-4 flex justify-center items-center flex-col text-center"
            v-if="planLoading || Object.keys(currPlan).length === 0"
          >
            <PuSkeleton
              class="h-full w-full z-20 rounded-xl absolute"
              v-if="planLoading"
            />
            <div v-if="Object.keys(currPlan).length === 0 && !planLoading">
              <div>Subscribe a plan to unlock the statistics.</div>
              <inertia-link
                :href="$route('pricing') + '#products'"
                class="link mt-2"
                >Go to Plans</inertia-link
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </layout>
</template>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../../Components/Layout/layout.vue");

const sideMenu = () =>
  import(
    /* webpackChunkName: "sideMenu" */ /* webpackPrefetch: true */ "../../Components/Layout/User/sideMenu.vue"
  );
const barChart = () =>
  import(
    /* webpackChunkName: "barChart" */ /* webpackPrefetch: true */ "../../Components/Layout/User/statisticsBarChart.vue"
  );

const lineChartDay = () =>
  import(
    /* webpackChunkName: "lineChart" */ /* webpackPrefetch: true */ "../../Components/Layout/User/statisticsLineChartDay.vue"
  );

const alertBox = () =>
  import(/* webpackChunkName: "alertBox" */ "../../Components/alertBox.vue");

const CalendarIcon = () =>
  import(/* webpackChunkName: "CalendarIcon" */ "@vue-hero-icons/outline").then(
    (Import) => Import.CalendarIcon
  );

const pagination = () =>
  import(
    /* webpackChunkName: "pagination" */ "../../Components/Layout/pagination.vue"
  );

import MonthPickerInput from "../../Components/month-picker/monthPickerInput.vue";

export default {
  components: {
    layout,
    sideMenu,
    barChart,
    lineChartDay,
    CalendarIcon,
    alertBox,
    pagination,
    MonthPickerInput,
  },
  props: {
    errors: Object,
  },
  data() {
    return {
      currPlan: {},
      statistics: null,
      statisticsDates: null,

      planLoading: false,
      remainderLoading: true,
      renderLine: 0,
      renderLineDaily: 0,
      usedApiCalls: 0,

      showLoading: true,
      showLoadingPlan: true,

      statisticsMonthLoading: true,
      statisticsDailyLoading: false,
      statisticsErrorMonthLoading: true,
      statisticsErrorDailyLoading: false,

      months: [],

      maxDate: null,
      minDate: null,

      //------------------------
      //  month-picker-input
      //------------------------
      d: new Date(),
      date: null,
      startDate: null,
      endDate: null,

      //------------------------
      //Month Chart
      //------------------------
      showDailyChart: false,
      monthConsumption: [],
      monthLabels: [],
      errorDataMonth: [],

      //------------------------
      //Daily Chart
      //------------------------
      dailyLabels: [],
      dailyConsumption: [],
      errorDataDaily: [],
    };
  },
  created() {
    this.getPlan();
    this.getConsumptionBetweenDates();
  },
  methods: {
    pad(value) {
      return value.toString().padStart(2, "0");
    },

    buildDate(year, month) {
      const date = new Date(year, month);
      return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}`;
    },

    showDate(date) {
      this.date = date;
      this.startDate = this.buildDate(
        this.date["year"],
        this.date["rangeFrom"]
      );

      this.endDate = this.buildDate(this.date["year"], this.date["rangeTo"]);

      this.getConsumptionBetweenDates();
    },

    visibilityDailyChart(data) {
      if (this.startDate === this.endDate && data[0].total_consumption > 0) {
        this.getConsumptionMonthly();
        this.showDailyChart = true;
      } else {
        this.showDailyChart = false;
      }
    },

    getPlan() {
      this.planLoading = true;
      this.axios
        .get("/user/api/plan")
        .then((response) => {
          if (response.data.plan_id) {
            this.currPlan = response.data;
          }
          this.planLoading = false;
        })
        .catch(() => {
          this.planLoading = false;
        });
    },

    getConsumptionBetweenDates() {
      const date = new Date();

      this.startDate = this.startDate
        ? this.startDate
        : this.buildDate(date.getFullYear(), date.getMonth());
      this.endDate = this.endDate
        ? this.endDate
        : this.buildDate(date.getFullYear(), date.getMonth());

      this.statisticsMonthLoading = true;

      this.axios
        .get(
          `/user/api/user-consumption-between-dates?startDate=${this.startDate}&endDate=${this.endDate}`
        )
        .then((response) => {
          if (response.data) {
            this.parseResponseConsumptionBetweenDates(response.data);

            this.visibilityDailyChart(response.data);
            this.statisticsMonthLoading = false;
            this.renderMonthsChart();
          }
        })
        .catch(() => {
          this.statisticsMonthLoading = false;
        });

      this.getErrorsBetweenDays();
    },
    renderMonthsChart() {
      if (!this.statisticsMonthLoading && !this.statisticsErrorMonthLoading) {
        this.renderLine++;
      }
    },
    renderDailyChart() {
      if (!this.statisticsDailyLoading && !this.statisticsErrorDailyLoading) {
        this.renderLineDaily++;
      }
    },
    getErrorsBetweenDays() {
      let startDate = new Date(this.startDate);
      let endDate = new Date(this.endDate);

      if (startDate.getTime() === endDate.getTime()) {
        endDate = new Date(
          Date.UTC(startDate.getFullYear(), startDate.getMonth() + 1, 0)
        );
      }
      this.statisticsErrorMonthLoading = true;
      this.axios
        .post("/user/api/get-statistics-errors-month", {
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
        })
        .then((response) => {
          if (response.data) {
            this.errorDataMonth = response.data;
          }
          this.statisticsErrorMonthLoading = false;
          this.renderMonthsChart();
        })
        .catch(() => {
          this.statisticsErrorMonthLoading = false;
        });
    },
    checkForValues(data) {
      return data[0].total_consumption;
    },

    parseResponseConsumptionBetweenDates(data) {
      data.sort((a, b) => {
        return a.month - b.month;
      });

      this.monthLabels = [];
      this.monthConsumption = [];
      data.map((item) => {
        this.monthLabels.push(item.monthName);
        this.monthConsumption.push(item.total_consumption);
      });
    },

    getConsumptionMonthly() {
      const date = new Date();

      this.startDate = this.startDate
        ? this.startDate
        : this.buildDate(date.getFullYear(), date.getMonth());
      this.endDate = this.endDate
        ? this.endDate
        : this.buildDate(date.getFullYear(), date.getMonth());

      this.statisticsDailyLoading = true;

      this.axios
        .get(
          `/user/api/user-consumption-monthly?startDate=${this.startDate}&endDate=${this.endDate}`
        )
        .then((response) => {
          if (response.data) {
            this.parseResponseConsumptionMonthly(response.data);
            this.statisticsDailyLoading = false;
            this.renderDailyChart();
          }
        })
        .catch(() => {
          this.statisticsDailyLoading = false;
        });
      this.getErrorsMonthly();
    },
    getErrorsMonthly() {
      let startDate = new Date(this.startDate);
      let endDate = new Date(
        Date.UTC(startDate.getFullYear(), startDate.getMonth() + 1, 0)
      );
      this.statisticsErrorDailyLoading = true;
      this.axios
        .post("/user/api/get-statistics-errors", {
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
        })
        .then((response) => {
          if (response.data) {
            this.errorDataDaily = response.data;
          }
          this.statisticsErrorDailyLoading = false;
          this.renderDailyChart();
        })
        .catch(() => {
          this.statisticsErrorDailyLoading = false;
        });
    },
    parseResponseConsumptionMonthly(data) {
      this.dailyLabels = [];
      this.dailyConsumption = [];
      data.map((item) => {
        this.dailyLabels.push(item.data);
        this.dailyConsumption.push(item.consumption);
      });
    },
  },
};
</script>

<style>
.month-picker-input {
  border-radius: 1rem 0 0 1rem !important;
  padding: 8px 15px !important;
}

.month-picker__month.selected {
  background-color: #492280;
  color: #ffffff;
}

.month-picker__month.selected-range-first {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.month-picker__month.selected-range-second {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.month-picker__month.selected-range {
  background-color: #f3eefa;
  color: #000;
  border-color: transparent;
}

.month-picker__year button {
  background-color: transparent;
  position: absolute;
  width: 5rem;
  height: 2.75rem;
  font-size: 2rem;
  border-radius: 5px;
  outline: none;
  border: none;
  top: 0.5rem;
  z-index: 2;
  color: #000;
}

.month-picker__container {
  width: 100%;
}

#line-chart {
  width: 100% !important;
  max-height: 400px;
}

#bar-chart {
  width: 100% !important;
  max-height: 400px;
}
</style>
 